import { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import { useApi } from "../../hooks/useApi";
import { useRefresh } from "../../contexts/RefreshContext";
import { listMyFiles, downloadFile } from "../../api/fileService";
import { formatDateShort } from "../../utils/formatters";
import { showApiError } from "../../utils/toast";

export default function FilesPage() {
  const { refreshKey } = useRefresh();
  const [view, setView] = useState("grid");
  const [folderId, setFolderId] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Searching is recursive across the entire drive — drop folder scope so the
  // breadcrumbs visibly reflect we're in search mode.
  const drive = useApi(
    () => {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      else if (folderId) params.folder_id = folderId;
      return listMyFiles(params);
    },
    [refreshKey, folderId, debouncedSearch],
  );

  const data = drive.data?.data || {};
  const folders = Array.isArray(data.folders) ? data.folders : [];
  const files = Array.isArray(data.files) ? data.files : [];
  const breadcrumbs = Array.isArray(data.breadcrumbs) ? data.breadcrumbs : [];
  const isSearching = !!debouncedSearch;

  const handleDownload = async (f) => {
    try {
      await downloadFile(f.id, f.original_name);
    } catch (err) {
      showApiError(err);
    }
  };

  const navigateTo = (id) => {
    setSearch("");
    setFolderId(id);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">My Files</h1>
          <div className="page-sub">Files shared by your CA team</div>
        </div>
        <div className="page-actions">
          <div className="row" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8, padding: 2 }}>
            <button className={`btn btn-sm ${view === "grid" ? "btn-primary" : "btn-ghost"}`} onClick={() => setView("grid")}>
              <Icon name="grid" size={13} />
            </button>
            <button className={`btn btn-sm ${view === "list" ? "btn-primary" : "btn-ghost"}`} onClick={() => setView("list")}>
              <Icon name="list" size={13} />
            </button>
          </div>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 12, gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div className="row" style={{ gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => navigateTo(null)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 8px" }}
            title="Drive root"
          >
            <Icon name="home" size={13} />
            <span className="fw-600">My drive</span>
          </button>
          {breadcrumbs.map((b, i) => {
            const last = i === breadcrumbs.length - 1;
            return (
              <span key={b.id} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Icon name="chevron-right" size={12} style={{ color: "var(--ink-400)" }} />
                {last ? (
                  <span className="fw-600" style={{ fontSize: 13 }}>{b.name}</span>
                ) : (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigateTo(b.id)}
                    style={{ padding: "4px 8px" }}
                  >
                    {b.name}
                  </button>
                )}
              </span>
            );
          })}
        </div>
        <div style={{ position: "relative", flex: 1, minWidth: 200, maxWidth: 360, marginLeft: "auto" }}>
          <Icon
            name="search"
            size={13}
            style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
          />
          <input
            className="input"
            style={{ paddingLeft: 30 }}
            placeholder="Search files & folders…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {isSearching && (
        <div className="text-xs t-muted" style={{ marginBottom: 8 }}>
          Showing search results across your whole drive.
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginLeft: 8 }}
            onClick={() => setSearch("")}
          >
            Clear search
          </button>
        </div>
      )}

      {drive.loading ? (
        <LoadingBlock rows={3} height={80} />
      ) : folders.length === 0 && files.length === 0 ? (
        <EmptyState
          icon="folder"
          title={isSearching ? "No matches" : "No files yet"}
          hint={isSearching ? "Try a different search." : "Files shared by your CA team will appear here."}
        />
      ) : view === "grid" ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }}>
          {folders.map((f) => (
            <button
              key={`folder-${f.id}`}
              type="button"
              className="card"
              onClick={() => navigateTo(f.id)}
              onDoubleClick={() => navigateTo(f.id)}
              style={{ overflow: "hidden", padding: 0, textAlign: "left", cursor: "pointer", background: "var(--surface)" }}
            >
              <div style={{ height: 110, background: "var(--primary-50)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="folder" size={36} style={{ color: "var(--primary-600)" }} />
              </div>
              <div style={{ padding: 12 }}>
                <div className="t-primary text-sm fw-600" title={f.name} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {f.name}
                </div>
                <div className="t-muted text-xs" style={{ marginTop: 4 }}>Folder</div>
              </div>
            </button>
          ))}
          {files.map((f) => (
            <div key={`file-${f.id}`} className="card" style={{ overflow: "hidden" }}>
              <div style={{ height: 110, background: "var(--surface-2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="file-text" size={36} style={{ color: "var(--ink-500)" }} />
              </div>
              <div style={{ padding: 12 }}>
                <div className="t-primary text-sm fw-500" title={f.original_name} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {f.original_name}
                </div>
                <div className="t-muted text-xs" style={{ marginTop: 4 }}>
                  {formatBytes(f.size)} · {formatDateShort(f.created_at)}
                </div>
                <div className="row" style={{ marginTop: 8, justifyContent: "flex-end" }}>
                  <button className="icon-btn" onClick={() => handleDownload(f)} title="Download">
                    <Icon name="download" size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="card-body no-pad">
            <table className="table">
              <thead><tr><th>Name</th><th>Size</th><th>Uploaded</th><th></th></tr></thead>
              <tbody>
                {folders.map((f) => (
                  <tr key={`folder-${f.id}`} style={{ cursor: "pointer" }} onClick={() => navigateTo(f.id)}>
                    <td className="t-primary">
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <Icon name="folder" size={14} style={{ color: "var(--primary-600)" }} />
                        <span className="fw-600">{f.name}</span>
                      </span>
                    </td>
                    <td className="t-muted">—</td>
                    <td className="t-muted">{formatDateShort(f.created_at)}</td>
                    <td></td>
                  </tr>
                ))}
                {files.map((f) => (
                  <tr key={`file-${f.id}`}>
                    <td className="t-primary">
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <Icon name="file-text" size={14} style={{ color: "var(--ink-500)" }} />
                        {f.original_name}
                      </span>
                    </td>
                    <td className="t-muted">{formatBytes(f.size)}</td>
                    <td className="t-muted">{formatDateShort(f.created_at)}</td>
                    <td>
                      <button className="icon-btn" onClick={() => handleDownload(f)} title="Download">
                        <Icon name="download" size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function formatBytes(b) {
  if (!b && b !== 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}
